import { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type MutableRefObject } from 'react';
import { useSearchParams } from 'react-router-dom';
import L from 'leaflet';
import {
  Castle, Crosshair, Eraser, Eye, EyeOff, Globe, Hand, History, Layers, Lightbulb, Loader2, MapPin, PaintBucket,
  Paintbrush, Pencil, Pipette, RefreshCw, SearchX, Square, TriangleAlert, Undo2, X,
} from 'lucide-react';
import * as api from '../worlds.api';
import { toast, useWorldsStore } from '../worlds.store';
import { MAX_BRUSH, MAX_OP_CELLS, fmtDate, fmtInt, loadPref, savePref } from '../constants';
import type { Bbox, ChunkOp, Country, GeoRegion, Rect } from '../worlds.types';
import { withGameGroup } from '../sharedMap';
import { useWorldMap } from '../map/useWorldMap';
import { MapFrame } from '../map/MapFrame';
import { bresenham, chunkFromKey, chunkKey, chunkRectBounds, latLngToChunk } from '../map/coords';
import { buildCenters, buildMarkers } from '../map/overlays';
import { CountryModal } from '../components/CountryModal';
import { FlagThumb } from '../components/FlagThumb';
import { worldsModalOpen } from '../components/Modal';
import {
  ActionButton, CardTitle, ColorChip, EmptyState, InfoBox, Kbd, SearchField, StatusBadge, cx,
} from '../components/ui';
import styles from '../../LootboxPage.module.scss';
import ws from '../WorldsPage.module.scss';
import mt from './MapTab.module.scss';

// ============================================================
// Onglet Carte : peintre de chunks par pays
// ============================================================
// Outils : Déplacer (sélection au clic, centres glissables), Pinceau (1 à 32 chunks, trait interpolé),
// Rectangle, Remplir (parcours côté serveur), Gomme (pinceau ou rectangle), Pipette.
// Un geste = une opération serveur (découpée en lots de 50 000 cellules au plus), envoyées une par
// une dans une file. Après chaque réponse, la grille est rattrapée en delta. Ctrl+Z annule la
// dernière opération de la session. Peindre n'écrase jamais le chunk d'un autre pays (ignoré).
//
// Colonne de droite comme le peintre de NG (WorldMapPainterTab) : Outil (grille de 3, aide du mode,
// gomme en rouge), Historique, Pays actif (fiche, région, liste), Couches (bascules mémorisées,
// légende des régions), Astuce. Paramètres d'URL lus : ?pays=<id> (cadrage sur un pays) et
// ?region=<id> (couche Régions allumée, cadrage sur la région), une fois chacun.
// ============================================================

type Tool = 'move' | 'brush' | 'rect' | 'fill' | 'erase' | 'pick';
/** Opération de grille faite dans cette session, avec son niveau (l'annulation vise ce niveau). */
interface SessionOp { level: string; id: string }

const TOOLS: { key: Tool; label: string; key2: string; icon: typeof Hand; write: boolean }[] = [
  { key: 'move', label: 'Déplacer', key2: 'V', icon: Hand, write: false },
  { key: 'brush', label: 'Pinceau', key2: 'B', icon: Paintbrush, write: true },
  { key: 'rect', label: 'Rectangle', key2: 'R', icon: Square, write: true },
  { key: 'fill', label: 'Remplir', key2: 'F', icon: PaintBucket, write: true },
  { key: 'erase', label: 'Gomme', key2: 'E', icon: Eraser, write: true },
  { key: 'pick', label: 'Pipette', key2: 'I', icon: Pipette, write: false },
];

/** Infobulle des boutons d'outil : ce que fait le geste. */
const HINTS: Record<Tool, string> = {
  move: 'Glisser pour déplacer la carte, molette pour zoomer. Clic sur un chunk : sélectionne son pays. Les centres des pays se glissent.',
  brush: 'Peindre en glissant, pinceau de 1 à 32 chunks. Les chunks d\'un autre pays sont ignorés, jamais écrasés.',
  rect: 'Glisser un rectangle calé sur les chunks. Les chunks d\'un autre pays sont ignorés, jamais écrasés.',
  fill: 'Clic à l\'intérieur d\'une zone fermée par les chunks du pays actif (fenêtre de 200 chunks autour du clic). Refusé si la zone touche un autre pays.',
  erase: 'Efface les chunks sous la gomme : ceux du pays actif, ou tous si « Pays actif uniquement » est décoché.',
  pick: 'Clic sur un chunk : son pays devient le pays actif, puis retour à l\'outil précédent.',
};

/** Aide courte de la surimpression. */
const SHORT_HINTS: Record<Tool, string> = {
  move: 'Glisser : déplacer la carte · clic : choisir un pays',
  brush: 'Glisser pour peindre',
  rect: 'Glisser un rectangle',
  fill: 'Clic dans une zone fermée',
  erase: 'Glisser pour effacer',
  pick: 'Clic sur un chunk : il devient le pays actif',
};

const OP_LABELS: Record<ChunkOp['type'], string> = {
  paint: 'Peinture', fill: 'Remplissage', erase: 'Gomme', 'clear-country': 'Pays vidé', undo: 'Annulation', import: 'Import',
};

// ---- Couches (mémorisées dans le navigateur) ----
type LayerKey = 'background' | 'chunks' | 'borders' | 'regions' | 'centers' | 'markers' | 'grid';
type LayerState = Record<LayerKey, boolean>;

const LAYERS_PREF = 'worlds.map.layers';
/** Régions allumées par défaut, comme chez NG (WMP:194). Fond de carte (image fixe) allumé. */
const LAYER_DEFAULTS: LayerState = {
  background: true, chunks: true, borders: true, regions: true, centers: true, markers: false, grid: false,
};

/** « Fond de carte » : listé seulement quand le niveau affiché a une image fixe et pas de tuiles. */
const LAYERS: { key: LayerKey; label: string; title: string }[] = [
  { key: 'background', label: 'Fond de carte', title: 'Image fixe du terrain sous les pays, en attendant les tuiles du niveau' },
  { key: 'chunks', label: 'Chunks peints', title: 'Territoire de chaque pays, à sa couleur (atténué si le pays est inactif)' },
  { key: 'borders', label: 'Frontières', title: 'Trait entre deux pays voisins, dès 4 px par chunk' },
  { key: 'regions', label: 'Régions', title: 'Contour, teinte et nom des géo-régions' },
  { key: 'centers', label: 'Centres des pays', title: 'Point d\'apparition (f home) de chaque pays ; glissable en mode Déplacer' },
  { key: 'markers', label: 'Marqueurs de biomes', title: 'Marqueurs du niveau (lecture seule ici, édition dans l\'onglet Marqueurs)' },
  { key: 'grid', label: 'Grille des chunks', title: 'Quadrillage des chunks au-dessus du terrain et des pays, dès 4 px par chunk' },
];

function loadLayers(): LayerState {
  const saved = loadPref<unknown>(LAYERS_PREF, null);
  const out = { ...LAYER_DEFAULTS };
  if (saved && typeof saved === 'object') {
    for (const k of Object.keys(LAYER_DEFAULTS) as LayerKey[]) {
      const v = (saved as Record<string, unknown>)[k];
      if (typeof v === 'boolean') out[k] = v;
    }
  }
  return out;
}

interface Gesture {
  shape: 'stroke' | 'rect';
  erase: boolean;
  countryId: string | null;
  start: { cx: number; cz: number };
  last: { cx: number; cz: number };
  pointerId: number;
  /** Cellules de CE trait (l'aperçu, lui, garde aussi celles des traits encore dans la file). */
  cells: Set<number>;
}

function matches(c: Country, q: string): boolean {
  if (!q) return true;
  const s = q.toLowerCase();
  return [c.slug, c.slugEn, c.empireNameFr, c.empireNameEn, c.isoCode].some((v) => !!v && v.toLowerCase().includes(s));
}

function isTyping(e: KeyboardEvent): boolean {
  const t = e.target as HTMLElement | null;
  return !!t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable);
}

/** Aide du mode sous la grille d'outils, selon l'état (gomme restreinte ou non). */
function toolHint(tool: Tool, readOnly: boolean, eraseOnlyActive: boolean, active: Country | null): string {
  if (tool === 'move' && readOnly) {
    return 'Glisser pour déplacer la carte, molette pour zoomer. Clic sur un chunk : sélectionne son pays.';
  }
  if (tool === 'erase') {
    if (!eraseOnlyActive) return 'Efface tous les chunks sous la gomme, quel que soit leur pays.';
    return active ? `Efface seulement les chunks de ${active.slug}.` : 'Efface seulement les chunks du pays actif.';
  }
  return HINTS[tool];
}

/** Échelle de la carte : 2^zoom px par chunk. */
function scaleText(zoom: number): string {
  return zoom >= 0 ? `${fmtInt(2 ** zoom)} px par chunk` : `1 px pour ${fmtInt(2 ** -zoom)} chunks`;
}

/** Emprise des chunks d'un ensemble de pays : grille locale (à jour), sinon bbox du satellite. */
function unionBbox(list: Country[]): Bbox | null {
  const cents = useWorldsStore.getState().grid?.centroids();
  let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
  for (const c of list) {
    const b = cents?.get(c.idx) ?? c.bbox;
    if (!b) continue;
    minX = Math.min(minX, b.minX);
    maxX = Math.max(maxX, b.maxX);
    minZ = Math.min(minZ, b.minZ);
    maxZ = Math.max(maxZ, b.maxZ);
  }
  return minX === Infinity ? null : { minX, maxX, minZ, maxZ };
}

// ---- Liste des pays (mémorisée : la carte relance des rendus à chaque zoom) ----
const CountryRows = memo(function CountryRows({ items, activeId, onPick, onCenter, rowRefs }: {
  items: Country[];
  activeId: string | null;
  onPick: (id: string) => void;
  onCenter: (c: Country) => void;
  rowRefs: MutableRefObject<Map<string, HTMLButtonElement>>;
}) {
  return (
    <>
      {items.map((c) => (
        <button key={c.id} type="button" ref={(el) => { if (el) rowRefs.current.set(c.id, el); else rowRefs.current.delete(c.id); }}
          className={cx(ws.countryRow, c.id === activeId && ws.countryRowOn, !c.enabled && ws.rowOff)}
          aria-pressed={c.id === activeId}
          onClick={() => onPick(c.id)} onDoubleClick={() => onCenter(c)}
          title={`${c.slug}${c.enabled ? '' : ' (inactif)'} · double-clic : centrer la carte`}>
          <FlagThumb flag={c.flag} width={20} showEmpty={false} />
          <ColorChip color={c.color} size={10} />
          <span className={ws.countryName}>{c.slug}</span>
          {c.chunkCount > 0 && <span className={ws.countryCount}>{fmtInt(c.chunkCount)}</span>}
        </button>
      ))}
    </>
  );
});

export function MapTab({ readOnly, focusCountryId }: { readOnly: boolean; focusCountryId?: string | null }) {
  const countries = useWorldsStore((s) => s.countries);
  const countriesLoaded = useWorldsStore((s) => s.countriesVersion !== -1);
  const geoRegions = useWorldsStore((s) => s.geoRegions);
  const geoRegionsLoaded = useWorldsStore((s) => s.geoRegionsVersion !== -1);
  const markers = useWorldsStore((s) => s.markers);
  const biomes = useWorldsStore((s) => s.biomes);
  const levelName = useWorldsStore((s) => s.level);
  const loading = useWorldsStore((s) => s.loading);
  const syncGrid = useWorldsStore((s) => s.syncGrid);
  const putCountry = useWorldsStore((s) => s.putCountry);
  const reloadCountries = useWorldsStore((s) => s.reloadCountries);
  const reloadGeoRegions = useWorldsStore((s) => s.reloadGeoRegions);
  const [params, setParams] = useSearchParams();
  const focusRegionId = params.get('region');

  const [tool, setToolState] = useState<Tool>('move');
  const [brush, setBrush] = useState(1);
  const [eraseShape, setEraseShape] = useState<'stroke' | 'rect'>('stroke');
  const [eraseOnlyActive, setEraseOnlyActive] = useState(true);
  const [activeId, setActiveId] = useState<string | null>(focusCountryId ?? null);
  const [search, setSearch] = useState('');
  const [layers, setLayers] = useState<LayerState>(loadLayers);
  const [pending, setPending] = useState(0);
  // Pile d'annulation de la session, par niveau : Ctrl+Z n'annule que sur le niveau affiché
  // (l'op est cherchée par {id, level} côté satellite).
  const [sessionOps, setSessionOps] = useState<SessionOp[]>([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [ops, setOps] = useState<ChunkOp[] | null>(null);
  const [editing, setEditing] = useState<Country | null>(null);
  const [savingRegion, setSavingRegion] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => { savePref(LAYERS_PREF, layers); }, [layers]);
  const toggleLayer = (k: LayerKey) => setLayers((l) => ({ ...l, [k]: !l[k] }));

  const wm = useWorldMap({
    showChunks: layers.chunks,
    showBorders: layers.borders,
    showRegions: layers.regions,
    showGridLines: layers.grid,
    showBackground: layers.background,
    labelClass: ws.regionLabel,
    labelDotClass: ws.regionLabelDot,
  });
  const { map, preview, focusBlock, focusChunks } = wm;

  const byId = useMemo(() => new Map(countries.map((c) => [c.id, c])), [countries]);
  const byIdx = useMemo(() => new Map(countries.map((c) => [c.idx, c])), [countries]);
  const active = activeId ? byId.get(activeId) ?? null : null;
  const levelOps = sessionOps.filter((o) => o.level === levelName).length;
  const visible = useMemo(() => countries.filter((c) => matches(c, search.trim())), [countries, search]);
  const painted = useMemo(() => {
    let chunks = 0;
    let withChunks = 0;
    for (const c of countries) { chunks += c.chunkCount; if (c.chunkCount > 0) withChunks++; }
    return { chunks, withChunks };
  }, [countries]);
  // Légende : régions qui ont au moins un pays peint sur ce niveau.
  const legend = useMemo(() => {
    const present = new Set(countries.filter((c) => c.geoRegionId && c.chunkCount > 0).map((c) => c.geoRegionId));
    return geoRegions.filter((r) => present.has(r.id));
  }, [countries, geoRegions]);
  const activeRegion = active?.geoRegionId ? geoRegions.find((r) => r.id === active.geoRegionId) ?? null : null;

  // ---- Valeurs lues par les écouteurs natifs (liés une seule fois par carte) ----
  const live = useRef({ tool, brush, eraseShape, eraseOnlyActive, active, readOnly, levelName, byIdx });
  live.current = { tool, brush, eraseShape, eraseOnlyActive, active, readOnly, levelName, byIdx };
  const prevToolRef = useRef<Tool>('brush');
  const gestureRef = useRef<Gesture | null>(null);
  const rectRef = useRef<L.Rectangle | null>(null);
  const hoverRef = useRef<L.Rectangle | null>(null);
  const previewRef = useRef(preview);
  previewRef.current = preview;
  const queueRef = useRef<Promise<void>>(Promise.resolve());
  const pendingRef = useRef(0);
  const sessionOpsRef = useRef<SessionOp[]>([]);
  const historyOpenRef = useRef(historyOpen);
  historyOpenRef.current = historyOpen;
  const rowRefs = useRef(new Map<string, HTMLButtonElement>());

  const setTool = useCallback((t: Tool) => {
    setToolState((cur) => { if (cur !== 'pick') prevToolRef.current = cur; return t; });
  }, []);

  // ---- Historique des opérations ----
  const loadOps = useCallback(async () => {
    const lvl = live.current.levelName;
    if (!lvl) return;
    const res = await api.listOps(lvl, 50);
    setOps(res.data?.items ?? []);
  }, []);
  useEffect(() => { if (historyOpen) void loadOps(); }, [historyOpen, loadOps, levelName]);

  const pushOp = (level: string, id: string) => {
    sessionOpsRef.current = [...sessionOpsRef.current, { level, id }];
    setSessionOps(sessionOpsRef.current);
  };

  /** File séquentielle : un geste n'est envoyé qu'après la réponse du précédent. */
  const enqueue = useCallback((job: () => Promise<void>) => {
    pendingRef.current++;
    setPending(pendingRef.current);
    queueRef.current = queueRef.current.then(job).catch((e) => {
      toast.error(e instanceof Error ? e.message : 'Opération en échec');
    }).then(() => {
      pendingRef.current--;
      setPending(pendingRef.current);
      if (pendingRef.current === 0 && !gestureRef.current) previewRef.current?.clear();
      if (historyOpenRef.current) void loadOps();
    });
  }, [loadOps]);

  // ---- Opérations ----
  const submitCells = useCallback((erase: boolean, countryId: string | null, keys: number[]) => {
    const lvl = live.current.levelName;
    if (!lvl || keys.length === 0) { previewRef.current?.clear(); return; }
    const flat: number[] = [];
    for (const k of keys) { const { cx, cz } = chunkFromKey(k); flat.push(cx, cz); }
    enqueue(async () => {
      let changed = 0;
      let skipped = 0;
      let failed = false;
      // Un long trait au gros pinceau peut dépasser 50 000 cellules : un lot = une opération.
      for (let i = 0; i < flat.length; i += MAX_OP_CELLS * 2) {
        const cells = flat.slice(i, i + MAX_OP_CELLS * 2);
        if (erase) {
          const res = await api.eraseChunks(lvl, { countryId, cells });
          if (!res.data) { toast.error(res.error ?? 'Gomme en échec'); failed = true; break; }
          if (res.data.opId) pushOp(lvl, res.data.opId);
          changed += res.data.erased;
        } else {
          const res = await api.paintChunks(lvl, { countryId: countryId!, cells });
          if (!res.data) { toast.error(res.error ?? 'Peinture en échec'); failed = true; break; }
          if (res.data.opId) pushOp(lvl, res.data.opId);
          changed += res.data.painted;
          skipped += res.data.skipped;
        }
      }
      if (failed && !changed && !skipped) { await syncGrid(); return; }
      if (erase) toast.info(changed ? `${fmtInt(changed)} chunks effacés` : 'Aucun chunk effacé');
      else if (changed || skipped) toast.success(`${fmtInt(changed)} chunks peints${skipped ? `, ${fmtInt(skipped)} ignorés (déjà à un autre pays)` : ''}`);
      else toast.info('Aucun chunk modifié (déjà à ce pays)');
      await syncGrid();
    });
  }, [enqueue, syncGrid]);

  const submitRect = useCallback((erase: boolean, countryId: string | null, rect: Rect) => {
    const lvl = live.current.levelName;
    if (!lvl) return;
    enqueue(async () => {
      if (erase) {
        const res = await api.eraseChunks(lvl, { countryId, rect });
        if (!res.data) { toast.error(res.error ?? 'Gomme en échec'); return; }
        if (res.data.opId) pushOp(lvl, res.data.opId);
        toast.info(res.data.erased ? `${fmtInt(res.data.erased)} chunks effacés` : 'Aucun chunk effacé');
      } else {
        const res = await api.paintChunks(lvl, { countryId: countryId!, rect });
        if (!res.data) { toast.error(res.error ?? 'Peinture en échec'); return; }
        if (res.data.opId) pushOp(lvl, res.data.opId);
        const { painted: n, skipped } = res.data;
        if (n || skipped) toast.success(`${fmtInt(n)} chunks peints${skipped ? `, ${fmtInt(skipped)} ignorés (déjà à un autre pays)` : ''}`);
        else toast.info('Aucun chunk modifié (déjà à ce pays)');
      }
      await syncGrid();
    });
  }, [enqueue, syncGrid]);

  const submitFill = useCallback((countryId: string, x: number, z: number) => {
    const lvl = live.current.levelName;
    if (!lvl) return;
    enqueue(async () => {
      const res = await api.fillChunks(lvl, { countryId, x, z });
      if (!res.data) { toast.error(res.error ?? 'Remplissage en échec'); return; }
      if (res.data.opId) pushOp(lvl, res.data.opId);
      toast.success(`${fmtInt(res.data.painted)} chunks remplis`);
      await syncGrid();
    });
  }, [enqueue, syncGrid]);

  const undo = useCallback((opId?: string) => {
    const lvl = live.current.levelName;
    // Sans opId (Ctrl+Z, bouton) : dernière op de la session SUR CE NIVEAU. Avec opId (historique) :
    // l'historique affiché est celui du niveau courant.
    const id = opId ?? sessionOpsRef.current.filter((o) => o.level === lvl).pop()?.id;
    if (!lvl || !id) { if (!opId) toast.info('Rien à annuler sur ce niveau dans cette session'); return; }
    enqueue(async () => {
      const res = await api.undoOp(lvl, id);
      // L'opération quitte la pile sauf échec réseau ou serveur : déjà annulée (409), non annulable
      // (400) ou inconnue (404), la réessayer ne servirait à rien.
      if (res.data || (res.status != null && res.status < 500)) {
        sessionOpsRef.current = sessionOpsRef.current.filter((o) => !(o.level === lvl && o.id === id));
        setSessionOps(sessionOpsRef.current);
      }
      if (!res.data) { toast.error(res.error ?? 'Annulation en échec'); return; }
      toast.success(`${fmtInt(res.data.restored)} chunks restaurés${res.data.conflicts ? `, ${fmtInt(res.data.conflicts)} en conflit (modifiés depuis)` : ''}`);
      await syncGrid();
    });
  }, [enqueue, syncGrid]);

  // ---- Geste : pinceau, rectangle, gomme ----
  const stamp = (cx: number, cz: number, size: number, out: number[]) => {
    const off = Math.floor((size - 1) / 2);
    for (let dz = 0; dz < size; dz++) for (let dx = 0; dx < size; dx++) out.push(chunkKey(cx - off + dx, cz - off + dz));
  };

  const cancelGesture = useCallback((): boolean => {
    const g = gestureRef.current;
    if (!g) return false;
    gestureRef.current = null;
    rectRef.current?.remove();
    rectRef.current = null;
    previewRef.current?.clear();
    map?.dragging.enable();
    const el = wm.containerRef.current;
    if (el?.hasPointerCapture(g.pointerId)) el.releasePointerCapture(g.pointerId);
    return true;
  }, [map, wm.containerRef]);

  useEffect(() => {
    const el = wm.containerRef.current;
    if (!map || !el) return;

    const shapeOf = (t: Tool, eraseShapeV: 'stroke' | 'rect'): 'stroke' | 'rect' | null =>
      t === 'brush' ? 'stroke' : t === 'rect' ? 'rect' : t === 'erase' ? eraseShapeV : null;

    const showHover = (e: PointerEvent) => {
      const v = live.current;
      const shape = v.readOnly ? null : shapeOf(v.tool, v.eraseShape);
      if (!shape || gestureRef.current) { hoverRef.current?.remove(); hoverRef.current = null; return; }
      const { cx, cz } = latLngToChunk(map.mouseEventToLatLng(e));
      const size = shape === 'stroke' ? v.brush : 1;
      const off = Math.floor((size - 1) / 2);
      const b = chunkRectBounds(cx - off, cx - off + size - 1, cz - off, cz - off + size - 1);
      const color = v.tool === 'erase' ? '#EF4444' : v.active?.color ?? '#FFFFFF';
      if (!hoverRef.current) {
        hoverRef.current = L.rectangle(b, { color, weight: 1, fill: false, interactive: false, pane: 'worldsPreview' }).addTo(map);
      } else {
        hoverRef.current.setBounds(b);
        hoverRef.current.setStyle({ color });
      }
    };

    const onDown = (e: PointerEvent) => {
      const v = live.current;
      if (v.readOnly || e.button !== 0) return;
      const shape = shapeOf(v.tool, v.eraseShape);
      if (!shape) return;
      if ((e.target as HTMLElement).closest('.leaflet-control')) return;
      const erase = v.tool === 'erase';
      const countryId = erase ? (v.eraseOnlyActive ? v.active?.id ?? null : null) : v.active?.id ?? null;
      if (!erase && !countryId) { toast.warning('Choisis d\'abord un pays actif (liste ou pipette, touche I).'); return; }
      if (erase && v.eraseOnlyActive && !countryId) {
        toast.warning('Aucun pays actif : décoche « Pays actif uniquement » pour tout effacer.');
        return;
      }
      // Annuler pointerdown supprime aussi le mousedown de compatibilité : Leaflet ne démarre pas
      // de déplacement. dragging.disable() couvre le reste du geste.
      e.preventDefault();
      e.stopPropagation();
      map.dragging.disable();
      el.setPointerCapture(e.pointerId);
      hoverRef.current?.remove();
      hoverRef.current = null;
      const start = latLngToChunk(map.mouseEventToLatLng(e));
      const g: Gesture = { shape, erase, countryId, start, last: start, pointerId: e.pointerId, cells: new Set() };
      gestureRef.current = g;
      if (shape === 'stroke') {
        const keys: number[] = [];
        stamp(start.cx, start.cz, v.brush, keys);
        keys.forEach((k) => g.cells.add(k));
        previewRef.current?.setColor(erase ? '#EF4444' : v.active?.color ?? '#FFFFFF');
        previewRef.current?.add(keys);
      } else {
        const color = erase ? '#EF4444' : v.active?.color ?? '#FFFFFF';
        rectRef.current?.remove();
        rectRef.current = L.rectangle(chunkRectBounds(start.cx, start.cx, start.cz, start.cz), {
          color, weight: 1, dashArray: '4 4', fillOpacity: 0.25, interactive: false, pane: 'worldsPreview',
        }).addTo(map);
      }
    };

    const onMove = (e: PointerEvent) => {
      const g = gestureRef.current;
      if (!g) { showHover(e); return; }
      if (e.pointerId !== g.pointerId) return;
      const cur = latLngToChunk(map.mouseEventToLatLng(e));
      if (cur.cx === g.last.cx && cur.cz === g.last.cz) return;
      if (g.shape === 'stroke') {
        const keys: number[] = [];
        const size = live.current.brush;
        bresenham(g.last.cx, g.last.cz, cur.cx, cur.cz, (x, z) => stamp(x, z, size, keys));
        keys.forEach((k) => g.cells.add(k));
        previewRef.current?.add(keys);
      } else {
        rectRef.current?.setBounds(chunkRectBounds(
          Math.min(g.start.cx, cur.cx), Math.max(g.start.cx, cur.cx), Math.min(g.start.cz, cur.cz), Math.max(g.start.cz, cur.cz),
        ));
      }
      g.last = cur;
    };

    const onUp = (e: PointerEvent) => {
      const g = gestureRef.current;
      if (!g || e.pointerId !== g.pointerId) return;
      gestureRef.current = null;
      if (el.hasPointerCapture(e.pointerId)) el.releasePointerCapture(e.pointerId);
      map.dragging.enable();
      if (e.type === 'pointercancel') {
        rectRef.current?.remove();
        rectRef.current = null;
        if (pendingRef.current === 0) previewRef.current?.clear();
        return;
      }
      if (g.shape === 'stroke') {
        submitCells(g.erase, g.countryId, [...g.cells]);
      } else {
        rectRef.current?.remove();
        rectRef.current = null;
        submitRect(g.erase, g.countryId, {
          minX: Math.min(g.start.cx, g.last.cx), maxX: Math.max(g.start.cx, g.last.cx),
          minZ: Math.min(g.start.cz, g.last.cz), maxZ: Math.max(g.start.cz, g.last.cz),
        });
      }
    };

    const onLeave = () => { hoverRef.current?.remove(); hoverRef.current = null; };

    // Clic simple : sélection (Déplacer), remplissage, pipette. Leaflet ne l'émet pas après un déplacement.
    const onClick = (e: L.LeafletMouseEvent) => {
      const v = live.current;
      const { cx, cz } = latLngToChunk(e.latlng);
      const grid = useWorldsStore.getState().grid;
      const idx = grid ? grid.get(cx, cz) : 0;
      const hit = idx ? v.byIdx.get(idx) ?? null : null;
      if (v.tool === 'move' || v.tool === 'pick') {
        if (hit) { setActiveId(hit.id); setSearch((q) => (q && !matches(hit, q.trim()) ? '' : q)); }
        if (v.tool === 'pick') {
          if (!hit) toast.info('Aucun pays sur ce chunk.');
          // Retour à l'outil d'avant la pipette (Déplacer en lecture seule).
          const back = prevToolRef.current === 'pick' ? 'brush' : prevToolRef.current;
          setToolState(v.readOnly ? 'move' : back);
        }
        return;
      }
      if (v.tool === 'fill' && !v.readOnly) {
        if (!v.active) { toast.warning('Choisis d\'abord un pays actif (liste ou pipette, touche I).'); return; }
        submitFill(v.active.id, cx, cz);
      }
    };

    el.addEventListener('pointerdown', onDown, true);
    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerup', onUp);
    el.addEventListener('pointercancel', onUp);
    el.addEventListener('pointerleave', onLeave);
    map.on('click', onClick);
    return () => {
      el.removeEventListener('pointerdown', onDown, true);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerup', onUp);
      el.removeEventListener('pointercancel', onUp);
      el.removeEventListener('pointerleave', onLeave);
      map.off('click', onClick);
      hoverRef.current?.remove();
      hoverRef.current = null;
    };
  }, [map, wm.containerRef, submitCells, submitRect, submitFill]);

  // Curseur de la carte selon l'outil.
  useEffect(() => {
    const el = wm.containerRef.current;
    if (!el) return;
    el.style.cursor = tool === 'move' || readOnly ? '' : 'crosshair';
  }, [tool, readOnly, wm.containerRef, map]);

  // ---- Clavier : Ctrl+Z, raccourcis d'outils, taille du pinceau ----
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (isTyping(e) || worldsModalOpen() || editing) return;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
        if (readOnly) return;
        e.preventDefault();
        undo();
        return;
      }
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const k = e.key.toLowerCase();
      if (k === '[') { setBrush((b) => Math.max(1, b - 1)); return; }
      if (k === ']') { setBrush((b) => Math.min(MAX_BRUSH, b + 1)); return; }
      const t = TOOLS.find((x) => x.key2.toLowerCase() === k || (x.key === 'move' && k === 'm'));
      if (t && (!t.write || !readOnly)) setTool(t.key);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [readOnly, undo, setTool, editing]);

  // ---- Centres des pays ----
  const centersRef = useRef<L.LayerGroup | null>(null);
  useEffect(() => {
    if (!map) return;
    const g = L.layerGroup();
    centersRef.current = g;
    return () => { g.remove(); centersRef.current = null; };
  }, [map]);

  // Glisser un centre : X et Z changent, Y repasse à vide (sol recalculé en jeu), yaw et pitch sont
  // gardés. Le satellite remet l'orientation à null quand le centre bouge sans centerY : on renvoie
  // donc les valeurs actuelles (même règle que la fiche pays, et que NG).
  const moveCenter = useCallback(async (c: Country, x: number, z: number): Promise<boolean> => {
    if (c.centerX === x && c.centerZ === z) return true;
    const res = await api.patchCountry(c.id, { centerX: x, centerZ: z, centerYaw: c.centerYaw, centerPitch: c.centerPitch });
    if (!res.data) { toast.error(res.error ?? 'Déplacement du centre en échec'); return false; }
    putCountry(res.data);
    toast.success(`Centre de ${c.slug} : ${x}, ${z} (Y recalculé en jeu)`);
    return true;
  }, [putCountry]);

  useEffect(() => {
    const g = centersRef.current;
    if (!map || !g) return;
    if (!layers.centers) { g.remove(); return; }
    g.addTo(map);
    buildCenters(g, countries, {
      className: ws.centerDot,
      selectedId: activeId,
      draggable: !readOnly && tool === 'move',
      handlers: { onSelect: (c) => setActiveId(c.id), onMove: moveCenter },
    });
  }, [map, countries, activeId, tool, readOnly, layers.centers, moveCenter]);

  // ---- Marqueurs de biomes (lecture seule ici) ----
  const markersRef = useRef<L.LayerGroup | null>(null);
  useEffect(() => {
    if (!map) return;
    const g = L.layerGroup();
    markersRef.current = g;
    return () => { g.remove(); markersRef.current = null; };
  }, [map]);
  useEffect(() => {
    const g = markersRef.current;
    if (!map || !g) return;
    if (!layers.markers) { g.remove(); return; }
    g.addTo(map);
    buildMarkers(g, markers, new Map(biomes.map((b) => [b.name, b])), {
      className: ws.markerDot, selectedId: null, draggable: false,
      handlers: { onSelect: () => undefined, onMove: async () => false },
    });
  }, [map, markers, biomes, layers.markers]);

  // ---- Pays actif ----
  useEffect(() => {
    if (!activeId) return;
    rowRefs.current.get(activeId)?.scrollIntoView({ block: 'nearest' });
  }, [activeId]);

  const centerOn = useCallback((c: Country) => {
    if (c.centerX != null && c.centerZ != null) focusBlock(c.centerX, c.centerZ, 5);
    else if (c.bbox) focusChunks(c.bbox);
    else toast.info(`${c.slug} n'a ni centre ni chunk sur ce niveau.`);
  }, [focusBlock, focusChunks]);

  /** Cadre la carte sur une région (union des emprises de ses pays sur ce niveau). */
  const focusRegion = useCallback((r: GeoRegion): boolean => {
    const b = unionBbox(useWorldsStore.getState().countries.filter((c) => c.geoRegionId === r.id));
    if (!b) { toast.info(`La région ${r.name} n'a aucun pays peint sur ce niveau.`); return false; }
    focusChunks(b);
    return true;
  }, [focusChunks]);

  // « Voir sur la carte » depuis l'onglet Pays : cadrer une fois la carte, la liste et la grille
  // prêtes. La vue initiale (cadrage sur la grille) est posée par useWorldMap juste avant, dans le
  // même rendu : elle ne l'écrase donc pas.
  const gridLoaded = useWorldsStore((s) => !!s.grid?.loaded);
  const focusedRef = useRef(false);
  useEffect(() => {
    if (focusedRef.current || !focusCountryId || !map || !gridLoaded) return;
    const c = byId.get(focusCountryId);
    if (!c) return;
    focusedRef.current = true;
    setActiveId(c.id);
    centerOn(c);
  }, [focusCountryId, map, byId, centerOn, gridLoaded]);

  // « Voir sur la carte » depuis l'onglet Régions (?region=<id>) : couche Régions allumée et
  // cadrage sur la région, une fois par région demandée.
  const regionFocusRef = useRef<string | null>(null);
  useEffect(() => {
    if (!focusRegionId || regionFocusRef.current === focusRegionId) return;
    if (!map || !gridLoaded || !countriesLoaded || !geoRegionsLoaded) return;
    regionFocusRef.current = focusRegionId;
    const r = geoRegions.find((x) => x.id === focusRegionId);
    if (!r) { toast.warning('Région introuvable : elle a peut-être été supprimée.'); return; }
    setLayers((l) => (l.regions ? l : { ...l, regions: true }));
    focusRegion(r);
  }, [focusRegionId, map, gridLoaded, countriesLoaded, geoRegionsLoaded, geoRegions, focusRegion]);

  const setRegion = async (c: Country, geoRegionId: string | null) => {
    if ((c.geoRegionId ?? null) === geoRegionId) return;
    setSavingRegion(true);
    try {
      // Garde de concurrence : seulement si le pays est encore dans la région affichée ici.
      const res = await api.assignGeoRegion([c.id], geoRegionId, c.geoRegionId);
      if (!res.data) { toast.error(res.error ?? 'Affectation en échec'); return; }
      if (res.data.updated === 0 && (res.data.skipped ?? 0) > 0) {
        toast.warning(`${c.slug} a changé de région entre-temps : rien n'a été modifié`);
        await reloadCountries();
        return;
      }
      putCountry({ ...(useWorldsStore.getState().countries.find((x) => x.id === c.id) ?? c), geoRegionId });
      void reloadGeoRegions();
      toast.success(`${c.slug} : ${geoRegionId ? geoRegions.find((r) => r.id === geoRegionId)?.name ?? 'région' : 'sans région'}`);
    } finally {
      setSavingRegion(false);
    }
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      await Promise.all([reloadCountries(), reloadGeoRegions(), syncGrid()]);
    } finally {
      setRefreshing(false);
    }
  };

  const goTab = (tab: string) => setParams(withGameGroup({ tab }, params));

  const shape = tool === 'brush' ? 'stroke' : tool === 'erase' ? eraseShape : null;
  const writes = TOOLS.find((t) => t.key === tool)?.write ?? false;
  const toolWarn = readOnly || !writes || active
    ? null
    : tool !== 'erase'
      ? 'Choisis un pays actif (liste ou pipette, touche I).'
      : eraseOnlyActive ? 'Aucun pays actif : décoche « Pays actif uniquement » pour tout effacer.' : null;
  const listLoading = !countriesLoaded || (loading && countries.length === 0);

  const side = (
    <>
      <section className={ws.card}>
        <CardTitle icon={Crosshair}>Outil</CardTitle>
        {/* Lecture seule : Déplacer et Pipette seulement, en deux colonnes. */}
        <div className={readOnly ? ws.toolGrid2 : ws.toolGrid} role="toolbar" aria-label="Outils de la carte">
          {TOOLS.filter((t) => !readOnly || !t.write).map((t) => {
            const Icon = t.icon;
            const on = tool === t.key;
            return (
              <button key={t.key} type="button" aria-pressed={on}
                className={cx(ws.toolBtn, ws.toolBtnStack, on && ws.toolBtnOn, on && t.key === 'erase' && ws.toolBtnDanger)}
                onClick={() => setTool(t.key)} title={`${HINTS[t.key]} (touche ${t.key2})`}>
                <Icon size={18} aria-hidden />
                <span>{t.label}</span>
                <Kbd>{t.key2}</Kbd>
              </button>
            );
          })}
        </div>
        <p className={ws.toolHint}>{toolHint(tool, readOnly, eraseOnlyActive, active)}</p>
        {toolWarn && (
          <p className={cx(ws.toolHint, ws.textWarn, mt.warnLine)} role="status">
            <TriangleAlert size={12} aria-hidden /> {toolWarn}
          </p>
        )}
        {!readOnly && tool === 'erase' && (
          <div className={ws.toolOptions}>
            <div className={ws.segmented} role="group" aria-label="Forme de la gomme">
              <button type="button" className={eraseShape === 'stroke' ? ws.segOn : ''} aria-pressed={eraseShape === 'stroke'}
                onClick={() => setEraseShape('stroke')}>Pinceau</button>
              <button type="button" className={eraseShape === 'rect' ? ws.segOn : ''} aria-pressed={eraseShape === 'rect'}
                onClick={() => setEraseShape('rect')}>Rectangle</button>
            </div>
            <label className={ws.checkLine}>
              <input type="checkbox" checked={eraseOnlyActive} onChange={(e) => setEraseOnlyActive(e.target.checked)} />
              Pays actif uniquement
            </label>
          </div>
        )}
        {!readOnly && shape === 'stroke' && (
          <label className={ws.toolOptions}>
            <span className={styles.label}>Taille : {brush} chunk{brush > 1 ? 's' : ''} ([ et ])</span>
            <input type="range" min={1} max={MAX_BRUSH} value={brush} onChange={(e) => setBrush(Number(e.target.value))} />
          </label>
        )}
        {!readOnly && (
          <div className={ws.inlineActions}>
            <button type="button" className={styles.secondaryBtn} onClick={() => undo()} disabled={levelOps === 0}
              title="Annuler la dernière opération de la session sur ce niveau (Ctrl+Z)">
              <Undo2 size={13} /> Annuler{levelOps ? ` (${levelOps})` : ''}
            </button>
            <button type="button" className={`${styles.secondaryBtn} ${historyOpen ? ws.btnOn : ''}`} aria-pressed={historyOpen}
              onClick={() => setHistoryOpen((o) => !o)}>
              <History size={13} /> Historique
            </button>
          </div>
        )}
      </section>

      {historyOpen && (
        <section className={ws.card}>
          <CardTitle icon={History}
            actions={<ActionButton icon={X} title="Fermer l'historique" onClick={() => setHistoryOpen(false)} />}>
            Historique (50 dernières)
          </CardTitle>
          {!ops ? (
            <div className={mt.listLoading}><Loader2 size={16} className={ws.spinning} aria-hidden /> Chargement…</div>
          ) : ops.length === 0 ? (
            <EmptyState compact icon={History} title="Aucune opération"
              text="Les peintures, remplissages, gommes et annulations de ce niveau apparaîtront ici." />
          ) : (
            <ul className={ws.opList}>
              {ops.map((o) => {
                const c = o.countryId ? byId.get(o.countryId) : undefined;
                const undoable = !readOnly && !o.undone && o.type !== 'undo' && o.type !== 'import';
                return (
                  <li key={o.id} className={o.undone ? ws.opUndone : undefined}>
                    <div>
                      <strong>{OP_LABELS[o.type]}</strong>{c ? ` · ${c.slug}` : ''} · {fmtInt(o.changed)}
                      {o.skipped ? ` (+${fmtInt(o.skipped)} ignorés)` : ''}
                      <div className={styles.muted}>{fmtDate(o.at)} · {o.by}{o.undone ? ' · annulée' : ''}</div>
                    </div>
                    {undoable && (
                      <ActionButton icon={Undo2} title="Annuler cette opération" onClick={() => undo(o.id)} />
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      )}

      <section className={ws.card}>
        <CardTitle icon={MapPin}
          actions={(
            <ActionButton icon={RefreshCw} title="Rafraîchir les pays, les régions et la grille"
              busy={refreshing || loading} onClick={() => void refresh()} />
          )}>
          Pays actif
        </CardTitle>
        {!active ? (
          <p className={ws.toolHint}>Aucun : choisis un pays dans la liste, clique un chunk ou prends la pipette (touche I).</p>
        ) : (
          <div className={mt.activeCard} style={{ '--c': active.color } as CSSProperties}>
            <div className={mt.activeHead}>
              <FlagThumb flag={active.flag} width={64} />
              <div className={mt.activeText}>
                <div className={mt.activeName}>
                  <ColorChip color={active.color} size={20} title={active.color} />
                  <strong title={active.slug}>{active.slug}</strong>
                </div>
                <div className={mt.activeMeta}>
                  {fmtInt(active.chunkCount)} chunks · {active.centerX != null && active.centerZ != null
                    ? `centre ${active.centerX}, ${active.centerZ}`
                    : 'aucun f home'}
                </div>
                {(!active.enabled || active.centerInside === false) && (
                  <div className={mt.activeBadges}>
                    {!active.enabled && (
                      <StatusBadge tone="off" icon={EyeOff} title="Pays inactif : hors du roster fondable, frontières masquées en jeu">
                        inactif
                      </StatusBadge>
                    )}
                    {active.centerInside === false && (
                      <StatusBadge tone="warn" icon={TriangleAlert} title="Le centre (f home) est hors du territoire peint du pays">
                        centre hors territoire
                      </StatusBadge>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className={ws.inlineActions}>
              <button type="button" className={styles.secondaryBtn} onClick={() => centerOn(active)} title="Centrer la carte sur le pays">
                <Crosshair size={13} /> Centrer
              </button>
              <button type="button" className={styles.secondaryBtn} onClick={() => setEditing(active)}>
                <Pencil size={13} /> {readOnly ? 'Fiche' : 'Modifier'}
              </button>
            </div>
            <div className={styles.field} style={{ minWidth: 0 }}>
              <label className={cx(styles.label, mt.regionLabel)} htmlFor="worlds-map-region">
                <Layers size={11} aria-hidden /> Géo-région
                {savingRegion && <Loader2 size={11} className={ws.spinning} aria-label="Enregistrement" />}
              </label>
              <div className={mt.regionRow}>
                {activeRegion && <ColorChip color={activeRegion.color} size={16} title={activeRegion.name} />}
                <select id="worlds-map-region" className={styles.input} value={active.geoRegionId ?? ''}
                  disabled={readOnly || savingRegion} aria-busy={savingRegion || undefined}
                  onChange={(e) => void setRegion(active, e.target.value || null)}>
                  <option value="">(aucune)</option>
                  {geoRegions.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </div>
              {geoRegionsLoaded && geoRegions.length === 0 && (
                <span className={ws.fieldHint}>
                  Aucune région : crée-en une dans l'onglet{' '}
                  <button type="button" className={ws.linkBtn} onClick={() => goTab('regions')}>Régions</button>.
                </span>
              )}
            </div>
          </div>
        )}
        <SearchField value={search} onChange={setSearch} placeholder="Rechercher un pays" />
        <div className={ws.countryList}>
          {listLoading ? (
            <div className={mt.listLoading} role="status"><Loader2 size={16} className={ws.spinning} aria-hidden /> Chargement des pays…</div>
          ) : visible.length === 0 ? (search.trim() ? (
            <EmptyState compact icon={SearchX} title="Aucun pays ne correspond"
              actions={<button type="button" className={styles.secondaryBtn} onClick={() => setSearch('')}>Effacer la recherche</button>} />
          ) : (
            <EmptyState compact icon={Castle} title="Aucun pays"
              text="Crée les pays, ou importe-les depuis NG Java, dans l'onglet Pays."
              actions={(
                <button type="button" className={styles.secondaryBtn} onClick={() => goTab('pays')}>
                  <Castle size={14} /> Onglet Pays
                </button>
              )} />
          )) : (
            <CountryRows items={visible} activeId={activeId} onPick={setActiveId} onCenter={centerOn} rowRefs={rowRefs} />
          )}
        </div>
        {countriesLoaded && countries.length > 0 && (
          <span className={mt.listCount}>
            {visible.length === countries.length
              ? `${fmtInt(countries.length)} pays`
              : `${fmtInt(visible.length)} sur ${fmtInt(countries.length)} pays`}
            {' · double-clic : centrer'}
          </span>
        )}
      </section>

      <section className={ws.card}>
        <CardTitle icon={Layers}>Couches</CardTitle>
        <div className={mt.layerList}>
          {LAYERS.filter((l) => l.key !== 'background' || !!wm.staticBg).map((l) => {
            const on = layers[l.key];
            const Icon = on ? Eye : EyeOff;
            const title = l.key === 'background' && wm.staticBgState === 'error'
              ? `${l.title} (image introuvable : fond neutre)`
              : l.title;
            return (
              <Fragment key={l.key}>
                <button type="button" className={cx(ws.layerToggle, on && ws.layerToggleOn)} aria-pressed={on}
                  title={title} onClick={() => toggleLayer(l.key)}>
                  <Icon size={14} aria-hidden /> {l.label}
                </button>
                {l.key === 'regions' && on && (legend.length > 0 ? (
                  <ul className={ws.legend} aria-label="Régions présentes sur ce niveau">
                    {legend.map((r) => (
                      <li key={r.id} className={ws.legendItem}>
                        <button type="button" className={mt.legendBtn} onClick={() => focusRegion(r)} title={`Cadrer la carte sur ${r.name}`}>
                          <ColorChip color={r.color} size={10} />
                          <span>{r.name}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : geoRegionsLoaded && (
                  <p className={mt.legendEmpty}>Aucune région n'a de pays peint sur ce niveau.</p>
                ))}
              </Fragment>
            );
          })}
        </div>
      </section>

      <InfoBox icon={Lightbulb} title="Astuce" storageKey="worlds.map.tip">
        <ul className={mt.tipList}>
          {!readOnly && (
            <li>
              Pipette <Kbd>I</Kbd> : un clic sur un chunk prend son pays comme pays actif, puis revient à l'outil d'avant.
            </li>
          )}
          <li>
            Outils au clavier : <Kbd>V</Kbd>{readOnly ? <> et <Kbd>I</Kbd></> : <><Kbd>B</Kbd><Kbd>R</Kbd><Kbd>F</Kbd><Kbd>E</Kbd><Kbd>I</Kbd></>}
            {!readOnly && <> ; <Kbd>[</Kbd> et <Kbd>]</Kbd> pour la taille du pinceau ; <Kbd>Ctrl</Kbd>+<Kbd>Z</Kbd> pour annuler</>}.
          </li>
          <li>Double-clic sur un pays de la liste : la carte se centre dessus.</li>
          {!readOnly && (
            <li>En mode Déplacer, glisser le point d'un pays déplace son centre (Y recalculé en jeu, orientation gardée).</li>
          )}
          <li>Clic droit sur la carte : copier la commande /tp de l'endroit.</li>
          <li><Kbd>Échap</Kbd> annule le geste en cours, puis quitte le plein écran.</li>
        </ul>
      </InfoBox>
    </>
  );

  return (
    <>
      <MapFrame wm={wm} side={side} onEscape={cancelGesture}
        overlay={(
          <>
            {/* Pastille d'état au-dessus de l'encart : le haut de la carte porte la note du fond. */}
            <div className={ws.mapBottomLeft}>
              {pending > 0 && (
                <span className={ws.busyBadge} role="status">
                  <Loader2 size={12} className={ws.spinning} aria-hidden /> En cours… ({pending})
                </span>
              )}
              <div className={ws.mapInfoCard}>
                <div className={ws.mapInfoHead}>
                  <Globe size={14} aria-hidden />
                  <span>
                    <strong>{fmtInt(painted.chunks)}</strong> chunk{painted.chunks > 1 ? 's' : ''} peint{painted.chunks > 1 ? 's' : ''} sur ce niveau
                    {' · '}<strong>{fmtInt(painted.withChunks)}</strong> pays
                  </span>
                </div>
                {active && (
                  <div className={mt.infoLine}>
                    <ColorChip color={active.color} size={10} />
                    <span>{active.slug} : <strong>{fmtInt(active.chunkCount)}</strong> chunks</span>
                  </div>
                )}
                <span className={mt.infoMuted}>Échelle : {scaleText(wm.zoom)}</span>
                <span>{readOnly ? 'Lecture seule : outils de peinture masqués.' : SHORT_HINTS[tool]}</span>
              </div>
            </div>
          </>
        )} />
      {editing && (
        <CountryModal country={editing} geoRegions={geoRegions} readOnly={readOnly}
          onClose={() => setEditing(null)} onSaved={putCountry} />
      )}
    </>
  );
}
